import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ArrowLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { plainJP } from "@/components/JP";
import { F, Sel, inputCls, btnPrimary, btnGhost, LEVELS, AREAS } from "@/components/admin/ui";

const KEYS = ["A", "B", "C", "D"];
const EMPTY = { type: "bunpo", level: "N4", pool: "module", prompt_jp: "", prompt_id: "", options: KEYS.map((k) => ({ key: k, text: "" })), answer: "A", explanation_id: "", explanation_jp: "", passage_key: "", passage_jp: "", passage_id: "" };

function QuestionForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setOpt = (i, text) => setF({ ...f, options: f.options.map((o, j) => (j === i ? { ...o, text } : o)) });

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, options: f.options.filter((o) => o.text.trim()), passage_key: f.passage_key || null, passage_jp: f.passage_jp || null, passage_id: f.passage_id || null };
    try {
      if (f.id) await api.put(`/admin/questions/${f.id}`, body); else await api.post("/admin/questions", body);
      toast.success("Soal disimpan"); onSaved();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  return (
    <form onSubmit={submit} className="soft-card space-y-4 p-5" data-testid="question-form">
      <div className="grid gap-3 sm:grid-cols-3">
        <F label="Jenis"><Sel testid="question-type-select" value={f.type} onChange={(v) => setF({ ...f, type: v })} options={AREAS} /></F>
        <F label="Level"><Sel testid="question-level-select" value={f.level} onChange={(v) => setF({ ...f, level: v })} options={LEVELS} /></F>
        <F label="Pool"><Sel testid="question-pool-select" value={f.pool} onChange={(v) => setF({ ...f, pool: v })} options={["module", "assessment"]} /></F>
      </div>
      {f.type === "dokkai" && (
        <div className="grid gap-3 rounded-xl bg-muted/50 p-3">
          <F label="Passage key (sama untuk soal dari bacaan yang sama)"><input data-testid="question-passage-key-input" className={inputCls} value={f.passage_key} onChange={set("passage_key")} placeholder="p2" /></F>
          <F label="Bacaan JP (isi di soal pertama saja)"><textarea data-testid="question-passage-jp-input" rows={3} className={inputCls} value={f.passage_jp} onChange={set("passage_jp")} /></F>
          <F label="Terjemahan bacaan ID"><textarea data-testid="question-passage-id-input" rows={2} className={inputCls} value={f.passage_id} onChange={set("passage_id")} /></F>
        </div>
      )}
      <F label="Pertanyaan (JP) — furigana: [漢字|かんじ|N4]"><textarea data-testid="question-prompt-jp-input" required rows={2} className={inputCls} value={f.prompt_jp} onChange={set("prompt_jp")} /></F>
      <F label="Terjemahan / petunjuk (ID)"><input data-testid="question-prompt-id-input" className={inputCls} value={f.prompt_id} onChange={set("prompt_id")} /></F>
      <div className="grid gap-2 sm:grid-cols-2">
        {f.options.map((o, i) => <F key={o.key} label={`Opsi ${o.key}`}><input data-testid={`question-option-${o.key}-input`} className={inputCls} value={o.text} onChange={(e) => setOpt(i, e.target.value)} /></F>)}
      </div>
      <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
        <F label="Kunci"><Sel testid="question-answer-select" value={f.answer} onChange={(v) => setF({ ...f, answer: v })} options={KEYS} /></F>
        <F label="Pembahasan (ID)"><input data-testid="question-explanation-id-input" className={inputCls} value={f.explanation_id} onChange={set("explanation_id")} /></F>
      </div>
      <F label="Pembahasan (JP, opsional)"><input data-testid="question-explanation-jp-input" className={inputCls} value={f.explanation_jp} onChange={set("explanation_jp")} /></F>
      <div className="flex gap-2"><button data-testid="question-save-button" className={btnPrimary}>Simpan soal</button><button type="button" data-testid="question-cancel-button" onClick={onCancel} className={btnGhost}>Batal</button></div>
    </form>
  );
}

export function QuestionsAdmin({ focus = null, onBack = null }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState(focus?.type || "all");
  const load = () => api.get("/admin/questions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!focus || !list.length) return;
    const q = focus.id ? list.find((x) => x.id === focus.id) : null;
    setFilter(q ? q.type : focus.type || "all");
    setTimeout(() => document.getElementById(`qrow-${focus.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  }, [focus, list]);
  const remove = async (q) => {
    if (!window.confirm("Hapus soal ini?")) return;
    await api.delete(`/admin/questions/${q.id}`); toast.success("Soal dihapus"); load();
  };
  const shown = list.filter((q) => filter === "all" || q.type === filter);

  return (
    <div className="space-y-5" data-testid="admin-questions">
      {onBack && !editing && <button data-testid="question-back-insight" onClick={onBack} className={btnGhost}><ArrowLeft className="h-3.5 w-3.5" />Kembali ke Insight{focus?.id ? ` · soal ${focus.id}` : focus?.type ? ` · area ${focus.type}` : ""}</button>}
      {editing ? <QuestionForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /> : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex max-w-full flex-wrap gap-0.5 rounded-2xl border border-border bg-card p-1 text-xs">{["all", ...AREAS].map((a) => <button key={a} data-testid={`question-filter-${a}`} onClick={() => setFilter(a)} className={`rounded-full px-3 py-1.5 ${filter === a ? "bg-foreground text-background" : "text-muted-foreground"}`}>{a === "all" ? `Semua (${list.length})` : a}</button>)}</div>
          <button data-testid="question-new-button" onClick={() => setEditing(EMPTY)} className={btnPrimary}><Plus className="h-4 w-4" />Soal baru</button>
        </div>
      )}
      <div className="space-y-2">
        {shown.map((q) => (
          <div key={q.id} id={`qrow-${q.id}`} data-testid={`question-row-${q.id}`} className={`soft-card flex items-start justify-between gap-3 px-5 py-3.5 ${focus?.id === q.id ? "ring-2 ring-primary" : ""}`}>
            <div className="min-w-0">
              <p className="text-[11px] text-muted-foreground">{q.id} · {q.type} · {q.level} · {q.pool}{q.passage_key ? ` · ${q.passage_key}` : ""}</p>
              <p lang="ja" className="jp truncate text-sm">{plainJP(q.prompt_jp)}</p>
              <p className="truncate text-xs text-muted-foreground">{q.prompt_id} — kunci {q.answer}</p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button data-testid={`question-edit-${q.id}`} onClick={() => setEditing({ ...EMPTY, ...q, passage_key: q.passage_key || "", passage_jp: q.passage_jp || "", passage_id: q.passage_id || "", options: KEYS.map((k) => q.options.find((o) => o.key === k) || { key: k, text: "" }) })} className={btnGhost}><Pencil className="h-3.5 w-3.5" /></button>
              <button data-testid={`question-delete-${q.id}`} onClick={() => remove(q)} className={`${btnGhost} text-primary`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
